/**
 * Hotjar Service
 * Handles initialization and management of Hotjar session replays
 */

import hotjar from "@hotjar/browser";
import { HOTJAR_SITE_ID, HOTJAR_VERSION } from "../env-config";

/**
 * Initialize Hotjar with the configured site ID and version
 * Only initializes if HOTJAR_SITE_ID is provided in environment
 */
export const initializeHotjar = (): void => {
  // Only initialize if site ID is configured
  if (!HOTJAR_SITE_ID) {
    console.info("Hotjar: Site ID not configured, skipping initialization");
    return;
  }

  try {
    const siteId = parseInt(HOTJAR_SITE_ID, 10);
    const hotjarVersion = parseInt(HOTJAR_VERSION, 10);

    if (isNaN(siteId)) {
      console.error("Hotjar: Invalid site ID provided");
      return;
    }

    // Initialize Hotjar
    hotjar.init(siteId, hotjarVersion);
    console.info(
      `Hotjar: Initialized successfully (Site ID: ${siteId}, Version: ${hotjarVersion})`,
    );
  } catch (error) {
    console.error("Hotjar: Failed to initialize", error);
  }
};

/**
 * Identify a user in Hotjar
 * @param userId - Unique identifier for the user
 * @param userInfo - Additional user attributes
 */
export const identifyHotjarUser = (
  userId: string,
  userInfo?: Record<string, string | number | boolean>,
): void => {
  if (!HOTJAR_SITE_ID) return;

  try {
    hotjar.identify(userId, userInfo || {});
    console.debug(`Hotjar: User identified (${userId})`);
  } catch (error) {
    console.error("Hotjar: Failed to identify user", error);
  }
};

/**
 * Track a custom Hotjar event
 * @param eventName - Name of the event to track
 */
export const trackHotjarEvent = (eventName: string): void => {
  if (!HOTJAR_SITE_ID) return;

  try {
    hotjar.event(eventName);
    console.debug(`Hotjar: Event tracked (${eventName})`);
  } catch (error) {
    console.error("Hotjar: Failed to track event", error);
  }
};

/**
 * Update Hotjar state (for triggering specific recordings)
 * @param stateName - Name of the state
 */
export const updateHotjarState = (stateName: string): void => {
  if (!HOTJAR_SITE_ID) return;

  try {
    hotjar.stateChange(stateName);
    console.debug(`Hotjar: State changed (${stateName})`);
  } catch (error) {
    console.error("Hotjar: Failed to update state", error);
  }
};
